/**
 * Liste de redare — designerul de ecrane.
 *
 * O listă de redare este „ce se vede pe televizor": șablonul (culori, fonturi, așezare în pagină),
 * elementele care se rotesc (meniul, codul QR, o promoție, o fotografie, un videoclip, un mesaj de
 * bun venit) și tranziția dintre ele. Listele se creează o dată și se leagă apoi de ecrane, din
 * pagina „Ecrane".
 *
 * Două principii se văd direct în interfață:
 *   • nimic nu se inventează în browser — serverul validează și limitează fiecare valoare, iar
 *     interfața oferă doar variantele acceptate (nu poți alege 99 de coloane, pentru că serverul
 *     oricum le-ar limita la 4 și ai crede că s-a aplicat ceva ce nu s-a aplicat);
 *   • previzualizarea folosește EXACT culorile și fonturile alese, ca restaurantul să vadă ce a
 *     configurat înainte să se uite la televizorul din sală.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  useToast,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { relativeTime, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';

interface Playlist {
  id: string;
  public_id: string;
  name: string;
  restaurant_id: string;
  template_code: string;
  template_config: Record<string, any>;
  transition: string;
  transition_ms: number;
  is_active: boolean;
  updated_at: string;
  item_count: number;
  items?: PlaylistItem[];
}

interface PlaylistItem {
  id: string;
  item_type: string;
  title: string | null;
  body: string | null;
  duration_seconds: number;
  menu_id: string | null;
  promotion_id: string | null;
  asset_id: string | null;
  sort_order: number;
  asset_token?: string | null;
  promotion_name?: string | null;
}

interface Template {
  code: string;
  name: string;
  description: string | null;
  default_config: Record<string, any>;
  is_premium: boolean;
  locked: boolean;
}

interface Restaurant {
  id: string;
  name: string;
}

const ITEM_TYPES: Array<{ value: string; label: string; hint: string }> = [
  { value: 'menu', label: 'Meniul complet', hint: 'Toate secțiunile publicate, împărțite automat pe ecrane.' },
  { value: 'qr', label: 'Cod QR', hint: 'Clientul scanează de pe ecran și meniul îi apare pe telefon.' },
  { value: 'promotion', label: 'Promoție', hint: 'O ofertă activă din lista de promoții.' },
  { value: 'welcome', label: 'Bun venit', hint: 'Un mesaj scurt, cu numele restaurantului.' },
  { value: 'image', label: 'Imagine', hint: 'O fotografie din galerie.' },
  { value: 'video', label: 'Videoclip', hint: 'Se redă mut, până la capăt, apoi se trece mai departe.' },
  { value: 'text', label: 'Text', hint: 'Un anunț simplu: „Astăzi avem ciorbă de burtă".' },
];

const ITEM_LABEL: Record<string, string> = Object.fromEntries(ITEM_TYPES.map((type) => [type.value, type.label]));

const QR_POSITIONS = [
  { value: 'bottom-right', label: 'Jos, dreapta' },
  { value: 'bottom-left', label: 'Jos, stânga' },
  { value: 'top-right', label: 'Sus, dreapta' },
  { value: 'top-left', label: 'Sus, stânga' },
];

const FONTS = ['Inter', 'Fraunces', 'Georgia', 'system'];
const COLOR_LABELS: Record<string, string> = {
  background: 'Fundal',
  surface: 'Suprafață',
  text: 'Text',
  muted: 'Text secundar',
  accent: 'Accent',
};

function fontStack(name: string | undefined, kind: 'heading' | 'body'): string {
  if (name === 'Fraunces' || name === 'Georgia') return "Georgia, 'Times New Roman', serif";
  if (name === 'system') return "system-ui, -apple-system, 'Segoe UI', sans-serif";
  return kind === 'heading' ? "'Inter', system-ui, sans-serif" : "system-ui, -apple-system, sans-serif";
}

export function PlaylistsPage() {
  const session = useSession();
  const toast = useToast();

  const playlists = useResource<Playlist[]>(() => api.get<Playlist[]>('/playlists'), [session.activeAccountId]);
  const templates = useResource<Template[]>(() => api.get<Template[]>('/display-templates'), [session.activeAccountId]);
  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), [session.activeAccountId]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Playlist | null>(null);

  const canEdit = session.can('display.update');
  const templateName = (code: string) => templates.data?.find((template) => template.code === code)?.name ?? code;

  async function removePlaylist() {
    if (!removing) return;
    try {
      await api.del(`/playlists/${removing.id}`);
      toast.success('Lista a fost ștearsă');
      playlists.reload();
    } catch (error) {
      // Lista folosită de un ecran nu se șterge de sub el — serverul explică de ce.
      toast.error('Nu am putut șterge lista', errorMessage(error));
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Liste de redare"
        subtitle="Ce se vede pe ecranele din restaurant: șablon, conținut și tranziții."
        actions={
          canEdit ? (
            <Button icon={<Icon name="sparkles" />} onClick={() => setCreateOpen(true)}>
              Listă nouă
            </Button>
          ) : null
        }
      />

      {playlists.error ? <InlineAlert tone="danger" title="Nu am putut încărca listele">{playlists.error}</InlineAlert> : null}

      {playlists.loading && !playlists.data ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-40" />
          ))}
        </div>
      ) : playlists.data?.length ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {playlists.data.map((playlist) => (
            <Card key={playlist.id} className="flex flex-col gap-4 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-display text-title text-ink-950">{playlist.name}</p>
                  <p className="text-body-s text-ink-600">{templateName(playlist.template_code)}</p>
                </div>
                <Badge tone={playlist.is_active ? 'success' : 'neutral'}>{playlist.is_active ? 'activă' : 'oprită'}</Badge>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-body-s text-ink-600">
                <span className="nums">{playlist.item_count ?? 0} elemente</span>
                <span aria-hidden>·</span>
                <span>
                  {playlist.transition === 'none'
                    ? 'fără tranziție'
                    : playlist.transition === 'slide'
                      ? 'alunecare'
                      : 'estompare'}
                </span>
                <span aria-hidden>·</span>
                <span>modificată {relativeTime(playlist.updated_at)}</span>
              </div>

              <div className="mt-auto flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setEditing(playlist.id)}>
                  Deschide designerul
                </Button>
                {canEdit ? (
                  <Button variant="ghost" onClick={() => setRemoving(playlist)}>
                    Șterge
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Icon name="display" size={28} />}
          title="Nicio listă de redare încă"
          description="O listă de redare spune ecranului ce să afișeze: meniul, codul QR, o promoție sau un mesaj de bun venit. Creează prima listă și leag-o apoi de un ecran."
          action={
            canEdit ? (
              <Button icon={<Icon name="sparkles" />} onClick={() => setCreateOpen(true)}>
                Creează prima listă
              </Button>
            ) : null
          }
        />
      )}

      <CreatePlaylistModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        templates={templates.data ?? []}
        restaurants={restaurants.data ?? []}
        onCreated={(id) => {
          playlists.reload();
          setEditing(id);
        }}
      />

      {editing ? (
        <PlaylistDesigner
          playlistId={editing}
          templates={templates.data ?? []}
          canEdit={canEdit}
          onClose={() => {
            setEditing(null);
            playlists.reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={() => void removePlaylist()}
        title="Ștergi lista de redare?"
        description={
          removing
            ? `„${removing.name}" dispare din dashboard. Ecranele care o folosesc trec pe următoarea listă din program.`
            : ''
        }
        confirmLabel="Șterge lista"
        tone="danger"
      />
    </div>
  );
}

/* ─────────────────────────────── crearea unei liste ─────────────────────────────── */

function CreatePlaylistModal({
  open,
  onClose,
  onCreated,
  templates,
  restaurants,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  templates: Template[];
  restaurants: Restaurant[];
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [templateCode, setTemplateCode] = useState('classic_menu');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setFormError(null);
    setErrors({});
    setRestaurantId(restaurants[0]?.id ?? '');
  }, [open, restaurants]);

  async function submit() {
    setBusy(true);
    setErrors({});
    setFormError(null);
    try {
      const created = await api.post<Playlist>('/playlists', { restaurantId, name, templateCode });
      toast.success('Lista a fost creată', 'Acum alege ce se vede pe ecran.');
      onCreated(created.id);
      onClose();
    } catch (error) {
      const fields = fieldErrors(error);
      setErrors(fields);
      if (!Object.keys(fields).length) setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Listă de redare nouă"
      description="Alege un nume și un șablon. Restul se reglează în designer."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={() => void submit()} loading={busy} disabled={!name.trim() || !restaurantId}>
            Creează lista
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError ? <InlineAlert tone="danger" title="Nu am putut crea lista">{formError}</InlineAlert> : null}

        <Field label="Nume" required errors={errors.name} hint={'Ce va recunoaște ospătarul: „Meniul zilei", „Seara de vinerea".'}>
          {(props) => (
            <Input
              {...props}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Meniul zilei"
              autoFocus
            />
          )}
        </Field>

        <Field label="Restaurant" required errors={errors.restaurantId}>
          {(props) => (
            <Select {...props} value={restaurantId} onChange={(event) => setRestaurantId(event.target.value)}>
              {restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Șablon" hint="Felul în care arată ecranul. Se poate schimba oricând.">
          {(props) => (
            <Select {...props} value={templateCode} onChange={(event) => setTemplateCode(event.target.value)}>
              {templates.map((template) => (
                <option key={template.code} value={template.code} disabled={template.locked}>
                  {template.name}
                  {template.is_premium ? (template.locked ? ' — disponibil în planul Business' : ' — premium') : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────── designerul propriu-zis ─────────────────────────────── */

function PlaylistDesigner({
  playlistId,
  templates,
  canEdit,
  onClose,
}: {
  playlistId: string;
  templates: Template[];
  canEdit: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const playlist = useResource<Playlist>(() => api.get<Playlist>(`/playlists/${playlistId}`), [playlistId]);
  const [draft, setDraft] = useState<Playlist | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [addType, setAddType] = useState('menu');
  const [addTitle, setAddTitle] = useState('');
  const [addDuration, setAddDuration] = useState(20);
  const [itemBusy, setItemBusy] = useState(false);

  useEffect(() => {
    if (playlist.data) setDraft(structuredClone(playlist.data));
  }, [playlist.data]);

  const current = draft ?? playlist.data ?? null;
  const config = current?.template_config ?? {};
  const layout = config.layout ?? {};
  const colors = config.colors ?? {};
  const fonts = config.fonts ?? {};
  const typography = config.typography ?? {};
  const animations = config.animations ?? {};
  const qr = config.qr ?? {};

  const dirty = useMemo(
    () => Boolean(draft && playlist.data && JSON.stringify(draft) !== JSON.stringify(playlist.data)),
    [draft, playlist.data],
  );

  function patchConfig(group: string, patch: Record<string, unknown>) {
    setDraft((previous) =>
      previous
        ? {
            ...previous,
            template_config: {
              ...previous.template_config,
              [group]: { ...(previous.template_config?.[group] ?? {}), ...patch },
            },
          }
        : previous,
    );
  }

  async function save() {
    if (!current) return;
    setBusy(true);
    setErrors({});
    try {
      await api.patch(`/playlists/${current.id}`, {
        name: current.name,
        templateCode: current.template_code,
        templateConfig: current.template_config,
        transition: current.transition,
        transitionMs: current.transition_ms,
        isActive: current.is_active,
      });
      toast.success('Designul a fost salvat', 'Ecranele îl preiau în câteva secunde.');
      playlist.reload();
    } catch (error) {
      const fields = fieldErrors(error);
      setErrors(fields);
      if (!Object.keys(fields).length) toast.error('Nu am putut salva', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function addItem() {
    if (!current) return;
    setItemBusy(true);
    try {
      await api.post(`/playlists/${current.id}/items`, {
        itemType: addType,
        title: addTitle.trim() || null,
        durationSeconds: addDuration,
      });
      setAddTitle('');
      await playlist.reload();
    } catch (error) {
      toast.error('Nu am putut adăuga elementul', errorMessage(error));
    } finally {
      setItemBusy(false);
    }
  }

  async function moveItem(index: number, direction: -1 | 1) {
    if (!current?.items) return;
    const items = [...current.items];
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const first = items[index]!;
    items[index] = items[target]!;
    items[target] = first;
    setDraft((previous) => (previous ? { ...previous, items } : previous));
    try {
      await api.put(`/playlists/${current.id}/items/reorder`, { itemIds: items.map((item) => item.id) });
      await playlist.reload();
    } catch (error) {
      toast.error('Nu am putut salva ordinea', errorMessage(error));
      await playlist.reload();
    }
  }

  async function updateItem(itemId: string, patch: Record<string, unknown>) {
    if (!current) return;
    try {
      await api.patch(`/playlists/${current.id}/items/${itemId}`, patch);
      await playlist.reload();
    } catch (error) {
      toast.error('Nu am putut salva elementul', errorMessage(error));
    }
  }

  async function removeItem(itemId: string) {
    if (!current) return;
    try {
      await api.del(`/playlists/${current.id}/items/${itemId}`);
      await playlist.reload();
    } catch (error) {
      toast.error('Nu am putut șterge elementul', errorMessage(error));
    }
  }

  const readOnly = !canEdit;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={current ? `Designer: ${current.name}` : 'Designer'}
      description="Culorile, fonturile și elementele din care se compune ecranul. Se salvează la apăsarea butonului."
      footer={
        readOnly ? (
          <Button variant="ghost" onClick={onClose}>
            Închide
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Renunță
            </Button>
            <Button onClick={() => void save()} loading={busy} disabled={!dirty}>
              Salvează designul
            </Button>
          </>
        )
      }
    >
      {playlist.loading && !current ? (
        <Skeleton className="h-64" />
      ) : !current ? (
        <InlineAlert tone="danger" title="Lista nu a putut fi încărcată">
          {playlist.error ?? 'Încearcă din nou.'}
        </InlineAlert>
      ) : (
        // derularea o face `Modal` (antetul și butoanele rămân pe loc)
        <div className="flex flex-col gap-6">
          {/* Previzualizarea: exact ce se vede pe televizor */}
          <div
            data-preview="playlist"
            className="rounded-[var(--radius-lg)] border border-ink-200 p-5"
            style={{
              background: colors.background ?? '#FBFAF7',
              color: colors.text ?? '#14212E',
              fontFamily: fontStack(fonts.body, 'body'),
            }}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-title" style={{ fontFamily: fontStack(fonts.heading, 'heading') }}>
                Meniul zilei
              </span>
              <span className="text-body-s" style={{ color: colors.muted ?? '#7A8798' }}>
                {current.items?.length ?? 0} elemente ·{' '}
                {current.transition === 'none' ? 'fără tranziție' : current.transition}
              </span>
            </div>
            <div
              className="mt-3 grid gap-x-6 gap-y-1"
              style={{ gridTemplateColumns: `repeat(${Math.max(1, Number(layout.columns) || 1)}, minmax(0, 1fr))` }}
            >
              {[
                { name: 'Pizza Diavola', price: '39 lei', accent: true },
                { name: 'Salată grecească', price: '28 lei', accent: false },
                { name: 'Ciorbă de burtă', price: '32 lei', accent: false },
                { name: 'Papanași', price: '24 lei', accent: false },
              ].map((item) => (
                <div key={item.name} className="flex items-baseline justify-between gap-3 py-1" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <span style={item.accent ? { color: colors.accent ?? '#0F766E', fontWeight: 600 } : undefined}>{item.name}</span>
                  <span className="nums">{item.price}</span>
                </div>
              ))}
            </div>
            {qr.show === false ? null : (
              <p className="mt-4 text-body-s" style={{ color: colors.muted ?? '#7A8798' }}>
                {qr.caption || 'Scanează pentru meniu'} · QR{' '}
                {QR_POSITIONS.find((position) => position.value === qr.position)?.label.toLowerCase() ?? 'jos, dreapta'}
              </p>
            )}
          </div>

          {/* Șablon + tranziție */}
          <section className="flex flex-col gap-4">
            <h3 className="font-display text-title text-ink-950">Șablon și tranziție</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nume" errors={errors.name}>
                {(props) => (
                  <Input {...props} value={current.name} disabled={readOnly} onChange={(event) => setDraft({ ...current, name: event.target.value })} />
                )}
              </Field>
              <Field label="Șablon" hint="La schimbare, culorile pornesc de la noul șablon.">
                {(props) => (
                  <Select
                    {...props}
                    value={current.template_code}
                    disabled={readOnly}
                    onChange={(event) => {
                      const template = templates.find((entry) => entry.code === event.target.value);
                      setDraft({
                        ...current,
                        template_code: event.target.value,
                        template_config: template ? structuredClone(template.default_config) : current.template_config,
                      });
                    }}
                  >
                    {templates.map((template) => (
                      <option key={template.code} value={template.code} disabled={template.locked}>
                        {template.name}
                        {template.locked ? ' — plan Business' : template.is_premium ? ' — premium' : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Tranziția între elemente">
                {(props) => (
                  <Select
                    {...props}
                    value={current.transition}
                    disabled={readOnly}
                    onChange={(event) => setDraft({ ...current, transition: event.target.value })}
                  >
                    <option value="fade">Estompare (recomandat)</option>
                    <option value="slide">Alunecare din lateral</option>
                    <option value="none">Fără tranziție</option>
                  </Select>
                )}
              </Field>
              <Field label="Durata tranziției" hint={`${current.transition_ms} ms`}>
                {(props) => (
                  <Input
                    {...props}
                    type="range"
                    min={0}
                    max={2000}
                    step={50}
                    value={current.transition_ms}
                    disabled={readOnly}
                    onChange={(event) => setDraft({ ...current, transition_ms: Number(event.target.value) })}
                  />
                )}
              </Field>
            </div>
          </section>

          {/* Culori, fonturi, așezare */}
          <section className="flex flex-col gap-4">
            <h3 className="font-display text-title text-ink-950">Culori, fonturi, așezare</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {Object.keys(COLOR_LABELS).map((key) => (
                <label key={key} className="flex flex-col gap-1 text-body-s text-ink-600">
                  {COLOR_LABELS[key]}
                  <input
                    type="color"
                    value={String(colors[key] ?? '#FFFFFF')}
                    disabled={readOnly}
                    onChange={(event) => patchConfig('colors', { [key]: event.target.value.toUpperCase() })}
                    className="h-10 w-full cursor-pointer rounded-[var(--radius-sm)] border border-ink-200 bg-surface"
                  />
                </label>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Font titluri">
                {(props) => (
                  <Select {...props} value={fonts.heading ?? 'Inter'} disabled={readOnly} onChange={(event) => patchConfig('fonts', { heading: event.target.value })}>
                    {FONTS.map((font) => (
                      <option key={font} value={font}>
                        {font}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Font text">
                {(props) => (
                  <Select {...props} value={fonts.body ?? 'Inter'} disabled={readOnly} onChange={(event) => patchConfig('fonts', { body: event.target.value })}>
                    {FONTS.map((font) => (
                      <option key={font} value={font}>
                        {font}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Coloane" hint="Câte coloane are meniul pe ecran.">
                {(props) => (
                  <Select
                    {...props}
                    value={String(layout.columns ?? 2)}
                    disabled={readOnly}
                    onChange={(event) => patchConfig('layout', { columns: Number(event.target.value) })}
                  >
                    {[1, 2, 3, 4].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Produse pe ecran" hint="Meniul se împarte automat în pagini, nu se taie.">
                {(props) => (
                  <Select
                    {...props}
                    value={String(layout.maxItemsPerScreen ?? 12)}
                    disabled={readOnly}
                    onChange={(event) => patchConfig('layout', { maxItemsPerScreen: Number(event.target.value) })}
                  >
                    {[4, 6, 8, 10, 12, 16, 20, 24, 30, 40].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Mărimea imaginilor">
                {(props) => (
                  <Select {...props} value={String(layout.imageSize ?? 'md')} disabled={readOnly} onChange={(event) => patchConfig('layout', { imageSize: event.target.value })}>
                    <option value="sm">Mici</option>
                    <option value="md">Medii</option>
                    <option value="lg">Mari</option>
                  </Select>
                )}
              </Field>
              <Field label="Spațierea produselor">
                {(props) => (
                  <Select
                    {...props}
                    value={String(typography.itemSpacing ?? 'comfortable')}
                    disabled={readOnly}
                    onChange={(event) => patchConfig('typography', { itemSpacing: event.target.value })}
                  >
                    <option value="compact">Strânsă</option>
                    <option value="comfortable">Normală</option>
                    <option value="spacious">Aerisită</option>
                  </Select>
                )}
              </Field>
            </div>

            <div className="flex items-center justify-between gap-4 rounded-[var(--radius-md)] border border-ink-200 p-4">
              <div>
                <p className="text-body text-ink-900">Imagini la produse</p>
                <p className="text-body-s text-ink-600">Fotografiile apar lângă produse, dacă există.</p>
              </div>
              <Switch
                checked={layout.showImages === true}
                onChange={(checked) => patchConfig('layout', { showImages: checked })}
                label="Imagini la produse"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Mărimea titlurilor" hint={`${Math.round((typography.headingScale ?? 1) * 100)}%`}>
                {(props) => (
                  <Input
                    {...props}
                    type="range"
                    min={80}
                    max={160}
                    step={5}
                    value={Math.round((typography.headingScale ?? 1) * 100)}
                    disabled={readOnly}
                    onChange={(event) => patchConfig('typography', { headingScale: Number(event.target.value) / 100 })}
                  />
                )}
              </Field>
              <Field label="Animația la intrare">
                {(props) => (
                  <Select {...props} value={animations.enter ?? 'fade'} disabled={readOnly} onChange={(event) => patchConfig('animations', { enter: event.target.value })}>
                    <option value="fade">Estompare</option>
                    <option value="slideUp">Ridicare ușoară</option>
                    <option value="none">Fără animație</option>
                  </Select>
                )}
              </Field>
            </div>
          </section>

          {/* Codul QR din colț */}
          <section className="flex flex-col gap-4">
            <h3 className="font-display text-title text-ink-950">Codul QR din colț</h3>
            <div className="flex items-center justify-between gap-4 rounded-[var(--radius-md)] border border-ink-200 p-4">
              <div>
                <p className="text-body text-ink-900">Arată codul QR pe ecrane</p>
                <p className="text-body-s text-ink-600">Un client care intră în restaurant poate scana direct de pe ecran.</p>
              </div>
              <Switch checked={qr.show !== false} onChange={(checked) => patchConfig('qr', { show: checked })} label="Arată codul QR" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Poziția">
                {(props) => (
                  <Select {...props} value={String(qr.position ?? 'bottom-right')} disabled={readOnly} onChange={(event) => patchConfig('qr', { position: event.target.value })}>
                    {QR_POSITIONS.map((position) => (
                      <option key={position.value} value={position.value}>
                        {position.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Textul de sub cod" hint="Maxim 60 de caractere.">
                {(props) => (
                  <Input
                    {...props}
                    value={String(qr.caption ?? '')}
                    maxLength={60}
                    disabled={readOnly}
                    placeholder="Scanează pentru meniu"
                    onChange={(event) => patchConfig('qr', { caption: event.target.value })}
                  />
                )}
              </Field>
            </div>
          </section>

          {/* Elementele care se rotesc */}
          <section className="flex flex-col gap-4">
            <h3 className="font-display text-title text-ink-950">Ce se rotește pe ecran</h3>

            <div className="flex flex-col divide-y divide-ink-100 rounded-[var(--radius-md)] border border-ink-200">
              {current.items?.length ? (
                current.items.map((item, index) => (
                  <div key={item.id} className="flex flex-wrap items-center gap-3 p-3">
                    <span className="nums w-6 text-body-s text-ink-400">{index + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-body text-ink-900">
                        {ITEM_LABEL[item.item_type] ?? item.item_type}
                        {item.title ? <span className="text-ink-500"> · {item.title}</span> : null}
                      </p>
                      <p className="text-body-s text-ink-500">{describeItem(item)}</p>
                    </div>
                    <label className="flex items-center gap-2 text-body-s text-ink-600">
                      secunde
                      <Input
                        type="number"
                        min={3}
                        max={600}
                        value={item.duration_seconds}
                        disabled={readOnly}
                        className="w-20"
                        onChange={(event) => void updateItem(item.id, { durationSeconds: Number(event.target.value) })}
                        aria-label={`Durata pentru ${ITEM_LABEL[item.item_type] ?? item.item_type}`}
                      />
                    </label>
                    {readOnly ? null : (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" aria-label="Mută mai sus" onClick={() => void moveItem(index, -1)} disabled={index === 0}>
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Mută mai jos"
                          onClick={() => void moveItem(index, 1)}
                          disabled={index === (current.items?.length ?? 1) - 1}
                        >
                          ↓
                        </Button>
                        <Button variant="ghost" size="sm" aria-label="Șterge elementul" onClick={() => void removeItem(item.id)}>
                          ✕
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="p-4 text-body-s text-ink-600">
                  Lista este goală. Adaugă cel puțin meniul, altfel ecranul nu are ce afișa.
                </p>
              )}
            </div>

            {readOnly ? null : (
              <div className="flex flex-wrap items-end gap-3 rounded-[var(--radius-md)] border border-dashed border-ink-300 p-4">
                <Field label="Element nou">
                  {(props) => (
                    <Select {...props} value={addType} onChange={(event) => setAddType(event.target.value)}>
                      {ITEM_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Titlu (opțional)">
                  {(props) => (
                    <Input {...props} value={addTitle} onChange={(event) => setAddTitle(event.target.value)} placeholder="Bun venit la Green Garden" />
                  )}
                </Field>
                <Field label="Secunde">
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={3}
                      max={600}
                      value={addDuration}
                      className="w-24"
                      onChange={(event) => setAddDuration(Number(event.target.value))}
                    />
                  )}
                </Field>
                <Button onClick={() => void addItem()} loading={itemBusy}>
                  Adaugă
                </Button>
                <p className="w-full text-body-s text-ink-500">{ITEM_TYPES.find((type) => type.value === addType)?.hint}</p>
              </div>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}

function describeItem(item: PlaylistItem): string {
  switch (item.item_type) {
    case 'menu':
      return 'Meniul publicat, împărțit automat pe ecrane.';
    case 'qr':
      return 'Codul QR al restaurantului, cu adresa permanentă.';
    case 'promotion':
      return item.promotion_name ? `Promoția „${item.promotion_name}".` : 'Promoția activă.';
    case 'image':
      return item.asset_token ? 'Imagine din galerie.' : 'Fără imagine aleasă încă.';
    case 'video':
      return item.asset_token ? 'Videoclip, redat fără sunet.' : 'Fără videoclip ales încă.';
    case 'welcome':
      return item.title ?? 'Mesaj de bun venit cu numele restaurantului.';
    default:
      return item.body ? item.body.slice(0, 80) : 'Text simplu.';
  }
}
